import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Loading, OverlayLoading } from '../components/Loading';
import MyReactTable from '../components/ReactTable';
import { TextOnly, Text, LangDictKey } from '../components/Text';
import Dialog from '@reach/dialog';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useFormik } from 'formik';
import { FilterPills } from '../components/FilterPills';
import { SECURITY_LEVEL_TYPE, SECURITY_LEVEL_TYPE_LIST } from '../CONSTANTS';
import { Row } from 'react-table';
import { toast } from 'react-toastify';
import AlertModal from '../components/AlertModal';
import { useTools } from '../context/Tools';
import HelpTooltip from '../components/HelpTooltip';
import SimpleTypeahead from '../components/SimpleTypeahead';

export const OurTools = () => {
  const { isLoadingTools, toolModels, toolsData, blockTool, unblockTool } =
    useTools();
  const [showOverlayLoading, setShowOverlayLoading] = useState<boolean>(false);

  const [models, setModels] = useState<any[]>([]);
  const [displayTools, setDisplayTools] = useState<any[]>([]);

  const [selectedTool, setSelectedTool] = useState<any>();
  const [showBlockToolModal, setShowBlockToolModal] = useState<boolean>(false);

  const [search, setSearch] = useState<string>('');

  const [modelFilter, setModelFilter] = useState<string[]>([]);
  const [isBlockedFilter, setIsBlockedFilter] = useState<string[]>([]);
  const [isRegisteredFilter, setIsRegisteredFilter] = useState<string[]>([]);
  const [securityLevelFilter, setSecurityLevelFilter] = useState<
    SECURITY_LEVEL_TYPE[]
  >([]);

  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);

  useEffect(() => {
    setModels(toolModels ?? []);
  }, [toolModels]);

  useEffect(() => {
    setDisplayTools(toolsData ?? []);
  }, [toolsData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const toggleFilterDialog = () => {
    setShowFilterDialog((prev) => !prev);
  };

  const resetFilters = () => {
    setModelFilter([]);
    setIsBlockedFilter([]);
    setIsRegisteredFilter([]);
    setSecurityLevelFilter([]);
    setShowFilterDialog(false);
  };

  const toggleBlockToolModal = () => {
    setShowBlockToolModal((prev) => !prev);
  };

  const handleOpenBlockToolModal = (tool: any) => {
    toggleBlockToolModal();
    setSelectedTool(tool);
  };

  const handleCloseBlockToolModal = () => {
    toggleBlockToolModal();
    setSelectedTool(null);
  };

  const handleBlockTool = async () => {
    if (selectedTool) {
      setShowOverlayLoading(true);

      const response = await blockTool(selectedTool);

      if (!response.error) {
        handleCloseBlockToolModal();
        toast.success(TextOnly('toolBlocked'), {
          containerId: 'standard',
        });
      } else {
        toast.error(response.error, {
          autoClose: false,
          containerId: 'standard',
        });
      }

      setShowOverlayLoading(false);
    }
  };

  const handleUnblockTool = async (tool: any) => {
    setShowOverlayLoading(true);

    const response = await unblockTool(tool);

    if (!response.error) {
      toast.success(TextOnly('toolUnblocked'), {
        containerId: 'standard',
      });
    } else {
      toast.error(response.error, {
        autoClose: false,
        containerId: 'standard',
      });
    }

    setShowOverlayLoading(false);
  };

  function multiFilter(
    rows: Array<Row>,
    columnId: string,
    filterValue: string
  ): Row[] {
    return rows.filter((row) => {
      return filterValue
        .toLowerCase()
        .includes(row.values[columnId].toLowerCase());
    });
  }

  const columns = [
    {
      Header: TextOnly('manufacturer'),
      accessor: 'toolManufacturer',
    },
    {
      Header: TextOnly('model'),
      accessor: 'toolModel',
      filter: multiFilter,
    },
    {
      Header: TextOnly('serial'),
      accessor: 'toolSerial',
    },
    {
      Header: TextOnly('isRegistered'),
      accessor: 'isRegistered',
      Cell: ({ row: { original } }: any) => (
        <>
          {original?.isRegistered === 'YES'
            ? TextOnly('yes').toUpperCase()
            : TextOnly('no').toUpperCase()}
        </>
      ),
    },
    {
      Header: TextOnly('securityLevel'),
      accessor: 'securityLevel',
      Cell: ({ row: { original } }: any) => (
        <>
          {original?.securityLevel === 'HIGH'
            ? TextOnly('high').toUpperCase()
            : original?.securityLevel === 'MEDIUM'
            ? TextOnly('medium').toUpperCase()
            : TextOnly('none').toUpperCase()}
        </>
      ),
      filter: multiFilter,
    },
    {
      id: 'isBlocked',
      Header: TextOnly('isBlocked'),
      accessor: 'blocked',
      Cell: ({ row: { original } }: any) => (
        <>
          {original?.blocked ?? false
            ? TextOnly('yes').toUpperCase()
            : TextOnly('no').toUpperCase()}
        </>
      ),
    },
    {
      Header: '',
      accessor: ' ',
      disableSortBy: true,
      Cell: ({ row: { original } }: any) => (
        <div className="l-flex-end l-flex-auto">
          <HelpTooltip
            label={
              original.blocked ? TextOnly('unblockTool') : TextOnly('blockTool')
            }
          >
            <button
              className="c-btn-icon"
              onClick={() => {
                if (original.blocked) {
                  handleUnblockTool(original);
                } else {
                  handleOpenBlockToolModal(original);
                }
              }}
            >
              <div className="c-btn__inner">
                {original.blocked ? (
                  <i className="c-btn__icon fal fa-shield-check" />
                ) : (
                  <i className="c-btn__icon fal fa-shield-slash" />
                )}
              </div>
            </button>
          </HelpTooltip>
        </div>
      ),
    },
  ];

  function handleFilterDelete(field: string, value: string) {
    if (field === 'model') {
      setModelFilter((prev) => prev.filter((model) => model !== value));
    } else if (field === 'isBlocked') {
      setIsBlockedFilter((prev) =>
        prev.filter((isBlocked) => isBlocked !== value)
      );
    } else if (field === 'isRegistered') {
      setIsRegisteredFilter((prev) =>
        prev.filter((isRegistered) => isRegistered !== value)
      );
    } else if (field === 'securityLevel') {
      setSecurityLevelFilter((prev) =>
        prev.filter((securityLevel) => securityLevel !== value)
      );
    }
  }

  const modelFilterSelections =
    modelFilter.length > 0
      ? {
          model: modelFilter.map((option) => ({
            key: option,
            value: option,
            label: option,
            pillLabel: option,
          })),
        }
      : null;

  const isBlockedFilterSelections =
    isBlockedFilter.length > 0
      ? {
          isBlocked: isBlockedFilter.map((option) => ({
            key: option,
            value: option.toLowerCase() === 'true',
            label: TextOnly(option.toLowerCase() as LangDictKey),
            pillLabel: TextOnly(
              option.toLowerCase() === 'true' ? 'blocked' : 'unblocked'
            ),
          })),
        }
      : null;

  const isRegisteredFilterSelections =
    isRegisteredFilter.length > 0
      ? {
          isRegistered: isRegisteredFilter.map((option) => ({
            key: option,
            value: option,
            label: TextOnly(option.toLowerCase() as LangDictKey),
            pillLabel: TextOnly(
              option.toLowerCase() === 'yes' ? 'registered' : 'unregistered'
            ),
          })),
        }
      : null;

  const securityLevelFilterSelections =
    securityLevelFilter.length > 0
      ? {
          securityLevel: securityLevelFilter.map((option) => ({
            key: option,
            value: option,
            label: option,
            pillLabel: option,
          })),
        }
      : null;

  const filters = {
    ...modelFilterSelections,
    ...isBlockedFilterSelections,
    ...isRegisteredFilterSelections,
    ...securityLevelFilterSelections,
  };

  const formik = useFormik({
    initialValues: {
      modelFilter: modelFilter,
      isBlockedFilter: isBlockedFilter,
      isRegisteredFilter: isRegisteredFilter,
      securityLevelFilter: securityLevelFilter,
    },
    onSubmit: async (values) => {
      setModelFilter(values.modelFilter);
      setIsBlockedFilter(values.isBlockedFilter);
      setIsRegisteredFilter(values.isRegisteredFilter);
      setSecurityLevelFilter(values.securityLevelFilter);
      toggleFilterDialog();
    },
    enableReinitialize: true,
  });

  const getFilteredTools = () => {
    const isBlockedFilters = (filters?.isBlocked || []).map((filter) =>
      String(filter?.value ?? '').toLocaleLowerCase()
    );
    const isRegisteredFilters = (filters?.isRegistered || []).map((filter) =>
      (filter?.value || '').toLocaleLowerCase()
    );
    const modelFilters = (filters?.model || []).map((filter) =>
      (filter?.value || '').toLocaleLowerCase()
    );
    const securityLevelFilters = (filters?.securityLevel || []).map((filter) =>
      (filter?.value || '').toLocaleLowerCase()
    );

    const toolsAux = [...displayTools].filter((tool) => {
      const matchesSearch = tool?.filterValue?.includes(
        search.toLocaleLowerCase()
      );

      const matchesIsBlocked =
        isBlockedFilters.length === 0 ||
        isBlockedFilters.includes(String(tool?.blocked).toLowerCase());

      const matchesIsRegistered =
        isRegisteredFilters.length === 0 ||
        isRegisteredFilters.includes(
          (tool?.isRegistered || '').toLocaleLowerCase()
        );

      const matchesModel =
        modelFilters.length === 0 ||
        modelFilters.includes((tool?.toolModel || '').toLocaleLowerCase());

      const matchesSecurityLevel =
        securityLevelFilters.length === 0 ||
        securityLevelFilters.includes(
          (tool?.securityLevel || '').toLocaleLowerCase()
        );

      return (
        matchesSearch &&
        matchesIsRegistered &&
        matchesIsBlocked &&
        matchesModel &&
        matchesSecurityLevel
      );
    });

    return toolsAux;
  };

  const handleDownloadCSV = () => {
    let lines: any = [];
    let csvData =
      'toolManufacturer,toolModel,toolSerial,isRegistered,securityLevel';
    const toolsAux = getFilteredTools();
    toolsAux.forEach((tool) => {
      lines.push(
        `${tool?.toolManufacturer}, ${tool?.toolModel}, ${tool?.toolSerial}, ${tool?.isRegistered}, ${tool?.securityLevel}`
      );
    });
    csvData = csvData + '\n' + lines.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    link.setAttribute(
      'download',
      `AutoAuthToolExport${year}-${month}-${day}.csv`
    );
    link.click();
    toast.success(TextOnly('toolsCSVExported'), {
      containerId: 'standard',
    });
  };

  if (isLoadingTools) {
    return <Loading />;
  }

  return (
    <>
      {showOverlayLoading && <OverlayLoading />}
      <Header title={TextOnly('ourTools')} />
      <div className="l-container">
        <div className="l-flex-wrap">
          <div className="l-flex">
            <button
              className="c-btn u-margin-right"
              onClick={handleDownloadCSV}
            >
              <div className="c-btn__inner">
                {Object.keys(filters).length || search?.length ? (
                  <Text tid="filteredToolCSV" />
                ) : (
                  <Text tid="toolCSV" />
                )}
              </div>
            </button>
            {Object.keys(filters).length ? (
              <div className="l-flex-wrap u-margin-top-large">
                <div className="l-flex-wrap">
                  <button
                    className="c-btn-link-text u-margin-right"
                    onClick={resetFilters}
                  >
                    <div className="c-btn__inner">
                      <Text tid="resetFilters" />
                    </div>
                  </button>
                  <FilterPills
                    filters={filters}
                    onFilterClick={handleFilterDelete}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="l-flex-between">
            <div className="l-flex-between">
              <button
                className="c-btn-outline u-margin-right"
                onClick={toggleFilterDialog}
              >
                <div className="c-btn__inner">
                  <Text tid="filterBy" />
                  <i className="c-btn__icon fas fa-caret-down" />
                </div>
              </button>
              <div className="c-field u-margin-bottom-none">
                <label
                  htmlFor="search"
                  className="c-field__label u-is-vishidden"
                >
                  <Text tid="search" />
                </label>
                <input
                  type="text"
                  id="search"
                  maxLength={50}
                  className="c-input"
                  placeholder={TextOnly('search')}
                  value={search}
                  onChange={handleChange}
                />
                <i className="c-field__input-icon fal fa-search" />
              </div>
            </div>
          </div>
        </div>

        <div className="u-margin-top-large">
          <MyReactTable
            columns={columns}
            data={displayTools}
            globalFilter={search}
            tableName="ourTools"
            filter={[
              {
                columnId: 'toolModel',
                filter:
                  modelFilter.length > 0 ? modelFilter.join('') : undefined,
              },
              {
                columnId: 'isBlocked',
                filter:
                  isBlockedFilter.length > 0
                    ? isBlockedFilter.join('')
                    : undefined,
              },
              {
                columnId: 'isRegistered',
                filter:
                  isRegisteredFilter.length > 0
                    ? isRegisteredFilter.join('')
                    : undefined,
              },
              {
                columnId: 'securityLevel',
                filter:
                  securityLevelFilter.length > 0
                    ? securityLevelFilter.join('')
                    : undefined,
              },
            ]}
          />
        </div>
        <AlertModal
          title={TextOnly('blockTool')}
          message={
            <Text
              tid="blockToolWarning"
              sub={{
                toolInfo: (
                  <span className="u-font-weight-bold">
                    {selectedTool?.toolSerial}
                  </span>
                ),
              }}
            />
          }
          showModal={showBlockToolModal}
          handleCancel={() => handleCloseBlockToolModal()}
          handleConfirm={() => handleBlockTool()}
          ConfirmBtnText={TextOnly('block')}
        />
        <Dialog
          isOpen={showFilterDialog}
          onDismiss={toggleFilterDialog}
          className="c-modal-slider"
          aria-label={TextOnly('filters')}
        >
          <button
            className="c-btn-icon c-modal-slider__close"
            onClick={toggleFilterDialog}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fa fa-times" />
            </div>
          </button>
          <h1 className="c-modal__heading">
            <Text tid="filters" />
          </h1>

          <div className="c-modal__body">
            <form onSubmit={formik.handleSubmit}>
              <div className="l-container-sm">
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="selectModel" />
                  </label>
                  <SimpleTypeahead
                    id={'modelFilter'}
                    placeholder={TextOnly('selectModel')}
                    options={models}
                    selected={formik.values.modelFilter}
                    onChange={(selected: any) => {
                      formik.setFieldValue('modelFilter', selected);
                    }}
                    multiple={true}
                  />
                </div>
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="isBlocked" />
                  </label>
                  <SimpleTypeahead
                    id={'isBlockedFilter'}
                    placeholder={TextOnly('isBlocked')}
                    options={['YES', 'NO'].map((option) => ({
                      label: TextOnly(
                        option.toLowerCase() as LangDictKey
                      ).toUpperCase(),
                      value: String(option === 'YES'),
                    }))}
                    selected={
                      formik.values.isBlockedFilter
                        ? formik.values.isBlockedFilter.map((option) => ({
                            label: TextOnly(
                              option.toLowerCase() as LangDictKey
                            ).toUpperCase(),
                            value: String(option === 'YES'),
                          }))
                        : []
                    }
                    onChange={(selected: any) => {
                      if (selected.length > 0) {
                        formik.setFieldValue(
                          'isBlockedFilter',
                          selected?.map((option: any) => option.value)
                        );
                      }
                    }}
                  />
                </div>
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="isRegistered" />
                  </label>
                  <SimpleTypeahead
                    id={'isRegisteredFilter'}
                    placeholder={TextOnly('isRegistered')}
                    options={['YES', 'NO'].map((option) => ({
                      label: TextOnly(
                        option.toLowerCase() as LangDictKey
                      ).toUpperCase(),
                      value: option,
                    }))}
                    selected={
                      formik.values.isRegisteredFilter
                        ? formik.values.isRegisteredFilter.map((option) => ({
                            label: TextOnly(
                              option.toLowerCase() as LangDictKey
                            ).toUpperCase(),
                            value: option,
                          }))
                        : []
                    }
                    onChange={(selected: any) => {
                      if (selected.length > 0) {
                        formik.setFieldValue(
                          'isRegisteredFilter',
                          selected?.map((option: any) => option.value)
                        );
                      }
                    }}
                  />
                </div>
                <div className="c-field">
                  <label className="c-field__label">
                    <Text tid="selectSecurityLevel" />
                  </label>
                  <SimpleTypeahead
                    id={'securityLevelFilter'}
                    placeholder={TextOnly('selectSecurityLevel')}
                    options={SECURITY_LEVEL_TYPE_LIST.map((option) => ({
                      label: TextOnly(
                        option.toLowerCase() as LangDictKey
                      ).toUpperCase(),
                      value: option,
                    }))}
                    selected={
                      formik.values.securityLevelFilter
                        ? formik.values.securityLevelFilter.map((option) => ({
                            label: TextOnly(
                              option.toLowerCase() as LangDictKey
                            ).toUpperCase(),
                            value: option,
                          }))
                        : []
                    }
                    onChange={(selected: any) => {
                      if (selected.length > 0) {
                        formik.setFieldValue(
                          'securityLevelFilter',
                          selected?.map((option: any) => option.value)
                        );
                      }
                    }}
                    multiple={true}
                  />
                </div>
                <div className="c-field l-flex-between">
                  <button
                    className="c-btn-outline u-margin-right"
                    type="button"
                    onClick={resetFilters}
                  >
                    <Text tid="resetFilters" />
                  </button>
                  <button className="c-btn" type="submit">
                    <Text tid="applyFilters" />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Dialog>
      </div>
    </>
  );
};
